import React from "react";
import PageHero, { PageCTA } from "../components/PageHero";
import { Vault, KeyRound, ShieldAlert, Check, Wrench, Phone } from "lucide-react";
import { heroImages, companyInfo } from "../data/mock";

const DepannageCoffreFort = () => {
  return (
    <div>
      <PageHero
        title="Dépannage coffre-fort Fichet"
        subtitle="Coffre-fort bloqué, clé perdue, combinaison oubliée ? Nos experts ouvrent tous les modèles Fichet-Bauche."
        breadcrumb="Dépannage coffre-fort"
        badge="Expertise coffre-fort"
        image={heroImages.depannageCoffre}
      />

      {/* INTRO */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Les experts du coffre-fort</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black leading-tight mb-6">
              Ouverture de coffre-fort sans destruction
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Fichet Serrurerie intervient sur tous les modèles de coffres-forts Fichet-Bauche, Hartmann Tresore et autres marques : coffres muraux, coffres à poser, armoires fortes et chambres fortes.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Nos techniciens sont formés aux techniques d'ouverture fine (manipulation, radiographie, endoscopie) permettant dans 90% des cas d'ouvrir votre coffre sans le détériorer.
            </p>
          </div>
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-40 h-40 bg-[#C9A227] z-0" />
            <img src={heroImages.depannageCoffre} alt="Coffre-fort Fichet" className="relative w-full h-[460px] object-cover z-10" />
          </div>
        </div>
      </section>

      {/* SITUATIONS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Toutes les situations</div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-black">Nos interventions coffre-fort</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: KeyRound, title: "Clé perdue ou volée", desc: "Ouverture manuelle puis remplacement du cylindre par un modèle Fichet." },
              { icon: ShieldAlert, title: "Combinaison oubliée", desc: "Manipulation du cadran et reset du code sur les coffres mécaniques et électroniques." },
              { icon: Wrench, title: "Mécanisme bloqué", desc: "Déblocage du pêne, réparation ou remplacement du mécanisme." },
              { icon: Vault, title: "Coffre après effraction", desc: "Remise en état ou remplacement complet après tentative de fracture." },
              { icon: KeyRound, title: "Pile épuisée (électronique)", desc: "Ouverture d'urgence et remplacement de la pile de secours." },
              { icon: ShieldAlert, title: "Héritage / succession", desc: "Ouverture avec procès-verbal notarié en cas de succession." },
            ].map((s, i) => (
              <div key={i} className="bg-white p-7 hover:shadow-xl transition-shadow border-t-4 border-[#C9A227]">
                <s.icon className="w-9 h-9 text-black mb-3" />
                <h3 className="text-lg font-bold text-black mb-2 tracking-tight">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TYPES */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Tous les modèles</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black">Nous intervenons sur tous les coffres</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "Coffres muraux", desc: "Encastrés dans le mur, discretion maximale.", image: heroImages.safe1 },
              { name: "Coffres à poser", desc: "Fixés au sol pour indépendance et flexibilité.", image: heroImages.safe2 },
              { name: "Armoires fortes", desc: "Grande capacité pour usage professionnel.", image: heroImages.depannageCoffre },
            ].map((t, i) => (
              <div key={i} className="group">
                <div className="h-64 overflow-hidden mb-4">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <h3 className="text-xl font-bold text-black tracking-tight mb-2">{t.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GUARANTEES */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 max-w-4xl">
          <div className="text-center mb-10">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Nos engagements</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black">Discrétion, expertise, garantie</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Techniciens expérimentés sur coffres Fichet-Bauche",
              "Ouverture sans destruction dans 90% des cas",
              "Discrétion absolue garantie",
              "Devis clair avant intervention",
              "Intervention rapide 7j/7",
              "Pièces d'origine et garantie constructeur",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white p-4">
                <div className="w-6 h-6 bg-[#C9A227] flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-black" />
                </div>
                <span className="text-gray-800 font-medium text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageCTA title="Coffre-fort bloqué ? Contactez nos experts." subtitle="Ouverture rapide et discrète par des techniciens spécialisés" />
    </div>
  );
};

export default DepannageCoffreFort;
